import React, { useState, useEffect, useCallback } from 'react';
import { X, Lock, Unlock, RefreshCw, AlertTriangle, ArrowRightLeft } from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { pickingService } from '../../../../services/pickingService';
import { PListLockHistoryDto, PListLockOperatorSummaryDto } from '../../../../types/picking';

interface PListLockHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Lagersjef-visning av lås/opplås-adferd på plukklister (ta-og-slipp-sporing,
 * Haucon Teamwork 36333980). Viser aggregert oversikt per operatør + siste
 * hendelser med holdetid. Datakilde: PListLockHistory (migrasjon 081).
 */
export const PListLockHistoryModal: React.FC<PListLockHistoryModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();

  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState<PListLockOperatorSummaryDto[]>([]);
  const [events, setEvents] = useState<PListLockHistoryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [operatorFilter, setOperatorFilter] = useState<string | null>(null);

  const loadData = useCallback(async (periodDays: number, operatorId: string | null) => {
    try {
      setLoading(true);
      setError(null);
      const [summaryData, eventData] = await Promise.all([
        pickingService.getLockHistorySummary(periodDays),
        pickingService.getLockHistory(periodDays, operatorId ?? undefined)
      ]);
      setSummary(summaryData);
      setEvents(eventData);
    } catch (err) {
      console.error('Error loading lock history:', err);
      setError(t('manager.modules.newPickingLists.lockHistory.loadError', {}, 'Kunne ikke laste låsehistorikk.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (isOpen) {
      loadData(days, operatorFilter);
    }
  }, [isOpen, days, operatorFilter, loadData]);

  if (!isOpen) return null;

  const actionBadge = (action: string) => {
    switch (action) {
      case 'Lock':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <Lock size={11} /> {t('manager.modules.newPickingLists.lockHistory.actionLock', {}, 'Tatt')}
          </span>
        );
      case 'Unlock':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            <Unlock size={11} /> {t('manager.modules.newPickingLists.lockHistory.actionUnlock', {}, 'Sluppet')}
          </span>
        );
      case 'ForceUnlock':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
            <AlertTriangle size={11} /> {t('manager.modules.newPickingLists.lockHistory.actionForceUnlock', {}, 'Tvangs-sluppet')}
          </span>
        );
      case 'Reassign':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
            <ArrowRightLeft size={11} /> {t('manager.modules.newPickingLists.lockHistory.actionReassign', {}, 'Omfordelt')}
          </span>
        );
      default:
        return <span className="text-xs text-gray-500">{action}</span>;
    }
  };

  const formatHeldMinutes = (minutes?: number): string => {
    if (minutes === undefined || minutes === null) return '-';
    if (minutes < 1) return '< 1 min';
    if (minutes < 60) return `${Math.round(minutes)} min`;
    return `${Math.floor(minutes / 60)}t ${Math.round(minutes % 60)}m`;
  };

  const formatTimestamp = (iso: string): string => {
    const d = new Date(iso);
    return d.toLocaleString('nb-NO', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Lock size={18} className="text-gray-600" />
            {t('manager.modules.newPickingLists.lockHistory.title', {}, 'Låsehistorikk (ta-og-slipp)')}
          </h2>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            >
              <option value={7}>{t('manager.modules.newPickingLists.lockHistory.last7Days', {}, 'Siste 7 dager')}</option>
              <option value={30}>{t('manager.modules.newPickingLists.lockHistory.last30Days', {}, 'Siste 30 dager')}</option>
              <option value={90}>{t('manager.modules.newPickingLists.lockHistory.last90Days', {}, 'Siste 90 dager')}</option>
            </select>
            <button
              onClick={() => loadData(days, operatorFilter)}
              className="p-2 rounded hover:bg-gray-100 transition-colors"
              disabled={loading}
            >
              <RefreshCw size={16} className={loading ? 'animate-spin text-gray-400' : 'text-gray-600'} />
            </button>
            <button onClick={onClose} className="p-2 rounded hover:bg-gray-100 transition-colors">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">{error}</div>
          )}

          {/* Per-operatør-oppsummering */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              {t('manager.modules.newPickingLists.lockHistory.summaryTitle', {}, 'Per operatør')}
            </h3>
            <div className="overflow-x-auto border border-gray-200 rounded">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('warehouse.general.operator', {}, 'Operatør')}</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('manager.modules.newPickingLists.lockHistory.colLocks', {}, 'Tatt')}</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('manager.modules.newPickingLists.lockHistory.colUnlocks', {}, 'Sluppet')}</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('manager.modules.newPickingLists.lockHistory.colForceUnlocks', {}, 'Tvang')}</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('manager.modules.newPickingLists.lockHistory.colReassigns', {}, 'Omfordelt')}</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('manager.modules.newPickingLists.lockHistory.colAvgHeld', {}, 'Snitt holdetid')}</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('manager.modules.newPickingLists.lockHistory.colLastActivity', {}, 'Sist aktiv')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {summary.map((row) => (
                    <tr
                      key={row.operatorID}
                      onClick={() => setOperatorFilter(operatorFilter === row.operatorID ? null : row.operatorID)}
                      className={`cursor-pointer hover:bg-blue-50 ${operatorFilter === row.operatorID ? 'bg-blue-50' : ''}`}
                    >
                      <td className="px-3 py-2 font-medium text-gray-900">{row.operatorID}</td>
                      <td className="px-3 py-2 text-right">{row.lockCount}</td>
                      <td className={`px-3 py-2 text-right ${row.unlockCount > 0 ? 'font-semibold text-orange-600' : ''}`}>{row.unlockCount}</td>
                      <td className="px-3 py-2 text-right">{row.forceUnlockCount}</td>
                      <td className="px-3 py-2 text-right">{row.reassignCount}</td>
                      <td className="px-3 py-2 text-right">{formatHeldMinutes(row.avgHeldMinutes)}</td>
                      <td className="px-3 py-2 text-right text-gray-600">{row.lastActivity ? formatTimestamp(row.lastActivity) : '-'}</td>
                    </tr>
                  ))}
                  {summary.length === 0 && !loading && (
                    <tr>
                      <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                        {t('manager.modules.newPickingLists.lockHistory.noData', {}, 'Ingen lås/opplås-hendelser i perioden.')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {t('manager.modules.newPickingLists.lockHistory.summaryHint', {}, 'Trykk på en rad for å filtrere hendelseslisten på operatøren.')}
            </p>
          </div>

          {/* Hendelsesliste */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              {t('manager.modules.newPickingLists.lockHistory.eventsTitle', {}, 'Siste hendelser')}
              {operatorFilter && (
                <span className="ml-2 text-xs font-normal text-blue-600">
                  ({operatorFilter} — {t('manager.modules.newPickingLists.lockHistory.clearFilter', {}, 'trykk raden igjen for å nullstille')})
                </span>
              )}
            </h3>
            <div className="overflow-x-auto border border-gray-200 rounded">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('manager.modules.newPickingLists.lockHistory.colTime', {}, 'Tidspunkt')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('manager.modules.newPickingLists.lockHistory.colPList', {}, 'Plukkliste')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('manager.modules.newPickingLists.lockHistory.colAction', {}, 'Handling')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('warehouse.general.operator', {}, 'Operatør')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('manager.modules.newPickingLists.lockHistory.colPrevious', {}, 'Forrige eier')}</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('manager.modules.newPickingLists.lockHistory.colHeld', {}, 'Holdetid')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {events.map((event) => (
                    <tr key={event.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{formatTimestamp(event.created)}</td>
                      <td className="px-3 py-2 font-medium text-gray-900">#{event.pListID}</td>
                      <td className="px-3 py-2">{actionBadge(event.action)}</td>
                      <td className="px-3 py-2">{event.operatorID || '-'}</td>
                      <td className="px-3 py-2 text-gray-600">{event.previousLockedBy || '-'}</td>
                      <td className="px-3 py-2 text-right">{formatHeldMinutes(event.heldMinutes)}</td>
                    </tr>
                  ))}
                  {events.length === 0 && !loading && (
                    <tr>
                      <td colSpan={6} className="px-3 py-6 text-center text-gray-500">
                        {t('manager.modules.newPickingLists.lockHistory.noData', {}, 'Ingen lås/opplås-hendelser i perioden.')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
